/**
 * Nav.jsx
 * ---------------------------------------------------------------------------
 * Fixed top navigation. Transparent over the hero, backdrop-blurred white
 * with a hairline border once the user scrolls.
 *
 * Handles two contexts:
 *   - On the home route ("/"): in-page section anchors smooth-scroll.
 *   - On any other route (e.g. /coconsolex/*): clicking a section anchor
 *     navigates back to "/" with a hash so Home can scroll on mount.
 *
 * The "CoConsoleX" link is a true route (peer to other anchored sections)
 * and renders with the stylized X accent.
 */

import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { tokens } from "../../lib/tokens.js";

const NAV_ITEMS = [
  { label: "Platform", anchor: "platform" },
  { label: "Individuals", anchor: "individuals" },
  { label: "Firms", anchor: "firms" },
  { label: "CoConsoleX", route: "/coconsolex" },
  { label: "Data At Rest", anchor: "data-at-rest" },
  { label: "Services", anchor: "services" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onCoConsoleX = pathname.startsWith("/coconsolex");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40 || onCoConsoleX);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [onCoConsoleX]);

  const goAnchor = (id) => {
    if (pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate({ pathname: "/", hash: `#${id}` });
    }
  };

  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        background: scrolled ? "rgba(255,255,255,0.96)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: scrolled
          ? `1px solid ${tokens.divider}`
          : "1px solid transparent",
        zIndex: 100,
        transition: "all 220ms ease",
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "20px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Wordmark — links to home */}
        <Link
          to="/"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            textDecoration: "none",
          }}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
            <rect x="0.5" y="0.5" width="21" height="21" stroke={tokens.ink} />
            <line x1="0" y1="11" x2="22" y2="11" stroke={tokens.ink} strokeWidth="0.8" />
            <line x1="11" y1="0" x2="11" y2="22" stroke={tokens.ink} strokeWidth="0.8" />
          </svg>
          <span
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 20,
              fontWeight: 500,
              color: tokens.ink,
              letterSpacing: "-0.01em",
            }}
          >
            CaseSignal<span style={{ color: tokens.accent }}>HQ</span>
          </span>
        </Link>

        {/* Links */}
        <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
          {NAV_ITEMS.map((item) =>
            item.route ? (
              <Link
                key={item.label}
                to={item.route}
                style={{
                  background: "none",
                  border: "none",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 13,
                  color: onCoConsoleX ? "#3D5A80" : tokens.ink,
                  cursor: "pointer",
                  letterSpacing: "0.02em",
                  padding: 0,
                  textDecoration: "none",
                  fontWeight: onCoConsoleX ? 600 : 400,
                }}
              >
                CoConsole
                <span style={{ color: "#3D5A80", fontWeight: 700 }}>X</span>
              </Link>
            ) : (
              <button
                key={item.anchor}
                onClick={() => goAnchor(item.anchor)}
                style={{
                  background: "none",
                  border: "none",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 13,
                  color: tokens.ink,
                  cursor: "pointer",
                  letterSpacing: "0.02em",
                  padding: 0,
                }}
              >
                {item.label}
              </button>
            ),
          )}
          <button
            onClick={() => goAnchor("individuals")}
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              color: "#fff",
              background: tokens.ink,
              border: "none",
              padding: "10px 18px",
              cursor: "pointer",
              letterSpacing: "0.04em",
            }}
          >
            Submit a Case
          </button>
        </div>
      </div>
    </nav>
  );
}
